import React, { useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { Linking, Platform } from 'react-native';
import InAppBrowser from 'utils/packages/inAppBrowser';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import { rem } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import { AppView, Button, Spacer } from 'design-system';
import EmptyComponent from 'design-system/EmptyComponent';
import { LoadingAppView } from 'design-system/LoadingAppView';
import BankLogo from 'features/pay/components/BankLogo';
import { mapToPaymentActivity } from 'features/pay/types/types';
import { getPaymentInfo, sendPaymentToTradingAccount, topUpGIAAccountFromBankAccount, depositToPotFromBankAccountOrConnectedAccount, buySecclAssetFromConnectedAccount, } from 'features/pay/actions';
import useFetch from 'hooks/useFetch';
import { useInterval } from 'hooks/timers';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import useAppRating from 'hooks/useAppRating';
import createStyleSheets from 'utils/createStyleSheets';
import Amplitude from 'utils/amplitude';
import Sentry from 'utils/sentry';
import { syncConnection, getSyncingStatus } from 'actions';
import { SYNC_SUCCESS } from 'actions/types';
import { DeepLinkType, parseDeepLink } from 'common/NavigatorActions/hooks/parseDeepLink';
import { selectIsSyncingManual } from 'reducers/selectors';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { selectPotFromPotId, selectShouldShowPotsList } from 'features/pots/reducers/selectors';
import { editVRPConsent, getVRPAuthorisationLink, getVRPConsents, makeVRPPayment, } from 'features/pre-approved-transfers/actions';
import { formatSortCodeAndAccountNumber } from 'utils';
import { depositedToPot, topUpPotUsingObCreated } from 'features/pots/actions';
import { fetchWealthAccounts, getConnectedAccount, pollPendingTopupsForTradingAccountId, topUpSecclAccountUsingObCreated, } from '../actions';
import { consumeInvestTopUpPin } from '../utils/investTopUpPin';
const strings = {
    topUpSuccessTitle: 'Payment sent',
    topUpSuccessDesc: 'You should see it in your account\nwithin the next few hours',
    cancelPaymentButton: 'Cancel Payment',
    cancelButton: 'Cancel',
};
const CreateTopUpScreen = ({ route, navigation }) => {
    const flow = route.params?.flow;
    const amount = route.params?.amount;
    const onError = route.params?.onError;
    const reference = route.params?.reference;
    const backCount = route.params?.backCount;
    const bankAccount = route.params?.bankAccount;
    const colors = useColors();
    const store = useAppStore();
    const rateApp = useAppRating();
    const isSyncingManual = useAppSelector(selectIsSyncingManual);
    const [authoriseVRPResult, authoriseVRPError, , authoriseVRP] = useFetch();
    const [createPaymentResult, createPaymentError, , createPayment] = useFetch();
    const [paymentInfoResult, paymentInfoError, paymentInfoBusy, fetchPaymentInfo] = useFetch();
    const [polling, setPolling] = useState(false);
    const dispatch = useAppDispatch();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            headerLeft: () => <EmptyComponent />,
        });
    }, [colors.text.primary]);
    useEffect(() => {
        let sub;
        const runInit = async () => {
            let consentId;
            if ('flow' in route.params && route.params.flow === 'topupSecclAccount') {
                const { bankAccount: topupBankAccount } = route.params;
                const bankAccountId = 'ukAccountNumber' in topupBankAccount && 'bankId' in topupBankAccount
                    ? topupBankAccount.bankId
                    : topupBankAccount.id;
                const { tradingAccountId } = route.params;
                if (amount) {
                    createPayment(topUpGIAAccountFromBankAccount({
                        bankAccountId,
                        tradingAccountId,
                        amount,
                    }));
                }
            }
            else if (route.params.flow === 'buySecclAssetFromBank') {
                createPayment(buySecclAssetFromConnectedAccount({
                    pin: consumeInvestTopUpPin(),
                    amount: route.params.amount,
                    assetId: route.params.assetId,
                    fundOrderWithOpenBanking: true,
                    fundOrderWithDirectDebit: false,
                    accountId: route.params.tradingAccountId,
                }));
            }
            else if ('perPaymentLimit' in route.params) {
                const { bankAccount: topupBankAccount } = route.params;
                // Editing or setting up pre-approved transfers
                if ('consentId' in route.params && route.params.consentId) {
                    //Editing VRP consent
                    const resp = await authoriseVRP(editVRPConsent(route.params.consentId, {
                        perPaymentLimit: route.params.perPaymentLimit,
                        periodicLimitAmount: route.params.periodicLimitAmount,
                        periodicLimitFrequency: route.params.periodicLimitFrequency,
                        periodAlignment: route.params.periodAlignment,
                    }));
                    consentId = resp?.consentId;
                }
                else {
                    // New VRP consent
                    const resp = await authoriseVRP(getVRPAuthorisationLink({
                        bankId: topupBankAccount.bankId,
                        perPaymentLimit: route.params.perPaymentLimit,
                        periodicLimitAmount: route.params.periodicLimitAmount,
                        periodicLimitFrequency: route.params.periodicLimitFrequency,
                        periodAlignment: route.params.periodAlignment,
                        sendFromAccountId: topupBankAccount.id,
                    }));
                    consentId = resp?.consentId;
                }
            }
            else if (route.params.flow === 'topUpPot') {
                const { amount, potId, bankAccount } = route.params;
                const accountId = 'accountNumber' in bankAccount ? bankAccount.id : undefined;
                createPayment(depositToPotFromBankAccountOrConnectedAccount({ potId, amount, accountId }));
            }
            else if (amount) {
                if ('consentId' in route.params && route.params.consentId) {
                    // Using an existing VRP consent to make payment
                    createPayment(makeVRPPayment(route.params.consentId, { amount, currency: 'GBP' }));
                    setPolling(true);
                }
                else {
                    //
                    createPayment(sendPaymentToTradingAccount({
                        amount,
                        accountId: bankAccount && 'id' in bankAccount ? bankAccount.id : undefined,
                        reference,
                    }));
                }
            }
            sub = Linking.addEventListener('url', async (event) => {
                if (!event.url ||
                    event.url.includes('onelink.emma-app.com') ||
                    event.url.includes('emma.onelink.me') ||
                    event.url.includes('awin.onelink.me') ||
                    event.url.includes('emmapublisher.onelink.me')) {
                    return;
                }
                const deepLinkData = parseDeepLink(event.url);
                switch (deepLinkData.type) {
                    case DeepLinkType.VRP_CONSENT_SUCCESS:
                        dispatch(getVRPConsents());
                        // They are setting up pre approved transfers without making a payment
                        if (!amount) {
                            navigation.navigateDeprecated('Success', {
                                title: 'Completed!',
                                subTitle: `Pre-approved transfer limits set for ${bankAccount.name} ${formatSortCodeAndAccountNumber(bankAccount.sortCode, bankAccount.accountNumber || undefined)}.`,
                                onContinue: () => {
                                    navigation.navigateDeprecated('PreApprovedTransfers');
                                    return true;
                                },
                            });
                        }
                        else if (consentId) {
                            createPayment(makeVRPPayment(consentId, { amount, currency: 'GBP' }));
                            setPolling(true);
                        }
                        break;
                    case DeepLinkType.PAYMENT_SUCCESS:
                        setPolling(true);
                        break;
                    case DeepLinkType.PAYMENT_FAIL:
                    case DeepLinkType.PAYMENT_ABORT:
                    case DeepLinkType.VRP_CONSENT_ABORT:
                    case DeepLinkType.VRP_CONSENT_FAIL:
                        navigation.goBack();
                        onError();
                        break;
                    default:
                        navigation.goBack();
                        break;
                }
                if (Platform.OS === 'ios' && !event.url.includes('/plaid')) {
                    InAppBrowser.close();
                }
            });
        };
        runInit();
        return () => {
            sub?.remove();
            setPolling(false);
        };
        // one-time effect
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useInterval(() => {
        if (!paymentInfoBusy && createPaymentResult) {
            fetchPaymentInfo(getPaymentInfo(createPaymentResult.paymentId));
        }
    }, polling ? 1000 : undefined);
    useEffect(() => {
        if (createPaymentError || authoriseVRPError) {
            Sentry.logError({
                filename: 'CreateTopUpScreen.tsx',
                message: 'Create Invest Account Top Up Payment failed',
                data: createPaymentError || authoriseVRPError,
            });
            if (createPaymentError && 'perPaymentLimit' in route.params && route.params.perPaymentLimit) {
                Modal.showError(createPaymentError?.errorMessage ||
                    'Your pre-approved transfer was setup successfully but the payment did not go through, please try again');
                navigation.navigateDeprecated('InvestSelectAccountToTopup', { flow: route.params.flow });
            }
            else {
                onError(createPaymentError?.errorMessage || authoriseVRPError?.errorMessage, createPaymentError?.errorType === 'PinError');
                navigation.goBack();
            }
        }
    }, [authoriseVRPError, createPaymentError, onError, route.params]);
    useEffect(() => {
        if (paymentInfoError) {
            Sentry.logError({
                filename: 'CreateTopUpScreen.tsx',
                message: 'Fetch Payment Info failed',
                data: paymentInfoError,
            });
        }
    }, [paymentInfoError]);
    useEffect(() => {
        if (createPaymentResult && createPaymentResult.link) {
            Linking.openURL(createPaymentResult.link);
        }
    }, [createPaymentResult]);
    useEffect(() => {
        if (authoriseVRPResult && authoriseVRPResult.link) {
            Linking.openURL(authoriseVRPResult.link);
        }
    }, [authoriseVRPResult]);
    const syncBank = useCallback(async () => {
        if ('bankConnectionId' in bankAccount) {
            let syncing = isSyncingManual;
            if (!isSyncingManual) {
                const syncAnswer = await dispatch(syncConnection(bankAccount.bankConnectionId, true));
                // @ts-ignore
                syncing = syncAnswer && syncAnswer.type === SYNC_SUCCESS && syncAnswer.payload.isSyncingManual;
            }
            if (syncing) {
                dispatch(getSyncingStatus());
            }
        }
    }, [bankAccount, isSyncingManual]);
    useEffect(() => {
        if (paymentInfoResult && (paymentInfoResult.status !== 'PENDING' || __DEV__)) {
            setPolling(false);
            syncBank();
            const payment = mapToPaymentActivity({
                ...paymentInfoResult,
                reference: null,
            }, {
                customTitle: strings.topUpSuccessTitle,
                customSubtitle: paymentInfoResult.status === 'COMPLETED' ? strings.topUpSuccessDesc : ' ',
            });
            if (payment) {
                Amplitude.logEvent('Completed Direct Payment', {
                    amount: paymentInfoResult.amount,
                    type: 'wealth_topup',
                    status: 'success',
                    destination: 'destination' in route.params ? route.params.destination : 'wallet',
                });
                if (flow === 'topUpPot') {
                    const transferAmount = route.params.amount;
                    const { potId, isCreatePotFlow } = route.params;
                    const potScreen = navigation.getState().routes?.find((route) => route.name === 'Pot');
                    const potFromScreenParams = potScreen?.params?.screenParams;
                    const potFromPotId = !potFromScreenParams ? selectPotFromPotId(store.getState(), potId) : undefined;
                    const pot = potFromPotId || potFromScreenParams;
                    if (pot) {
                        const depositEta = createPaymentResult && 'depositEta' in createPaymentResult ? createPaymentResult.depositEta : undefined;
                        if (potFromScreenParams) {
                            // @ts-ignore - a route taken from state is valid
                            navigation.popTo(potScreen.name, {
                                ...potScreen.params,
                                screenParams: {
                                    ...potScreen.params.screenParams, // this is deliberate to change the pot object in screen params to trigger fetching of activities
                                },
                            });
                        }
                        else {
                            navigation.popToTop();
                        }
                        dispatch(topUpPotUsingObCreated());
                        dispatch(getConnectedAccount());
                        dispatch(depositedToPot());
                        navigation.navigateDeprecated('DepositTimeline', {
                            depositEta,
                            emoji: pot.emoji,
                            potName: pot.name,
                            isBankTransfer: true,
                            amount: transferAmount,
                            potProductId: pot.productId,
                            timestamp: new Date().toISOString(),
                            // eslint-disable-next-line no-nested-ternary
                            potId: isCreatePotFlow ? (selectShouldShowPotsList(store.getState()) ? pot.id : undefined) : undefined,
                        });
                    }
                    else {
                        // this case should never be there
                        navigation.popToTop();
                    }
                    return;
                }
                if (flow === 'topupSecclAccount') {
                    dispatch(pollPendingTopupsForTradingAccountId(route.params.tradingAccountId));
                    dispatch(fetchWealthAccounts(3, route.params.tradingAccountId));
                    dispatch(topUpSecclAccountUsingObCreated());
                }
                else {
                    dispatch({ type: 'TOPUP_CREATED' }); // this tells topup saga to start polling for 2 minutes
                }
                if (route.params.flow === 'buySecclAssetFromBank') {
                    navigation.popToTop();
                    if (createPaymentResult?.order) {
                        navigation.navigate('DepositAndBuyAssetTimeline', {
                            assetId: createPaymentResult.order.assetId,
                            amount: {
                                value: route.params.amount,
                                currency: 'GBP',
                            },
                            timestamp: createPaymentResult.order.timestamp,
                            status: createPaymentResult.order.status,
                            fundingMethod: 'openBankingDeposit',
                        });
                    }
                    return;
                }
                if (flow === 'topupSecclAccount') {
                    const investSelectAccountToTopupRoute = navigation
                        .getState()
                        .routes?.find((route) => route.name === 'InvestSelectAccountToTopup');
                    navigation.pop(investSelectAccountToTopupRoute ? 3 : 2);
                    navigation.navigateDeprecated('PaymentActivity', {
                        payment,
                    });
                }
                else if (flow === 'recurringOrder') {
                    const stockScreenRoute = navigation.getState().routes?.find((route) => route.name === 'InvestStock');
                    const params = stockScreenRoute?.params;
                    if (params) {
                        navigation.navigateDeprecated('InvestStock', params);
                    }
                    navigation.navigateDeprecated('PaymentActivity', {
                        payment,
                    });
                }
                else {
                    let popCount = 4;
                    if ('supportsVrp' in bankAccount && bankAccount.supportsVrp) {
                        if (authoriseVRPResult) {
                            //User set up or edited a VRP
                            popCount += 2;
                        }
                    }
                    navigation.pop(popCount);
                    navigation.navigateDeprecated('PaymentActivity', {
                        payment,
                    });
                }
                if (paymentInfoResult.vrpConsentId) {
                    dispatch(getVRPConsents());
                }
                rateApp();
            }
        }
    }, [
        backCount,
        bankAccount,
        flow,
        paymentInfoResult,
        syncBank,
        rateApp,
        authoriseVRPResult,
        route.params,
        createPaymentResult,
        amount,
    ]);
    const styles = useStyles(styleSet);
    const onPress = () => {
        navigation.goBack();
    };
    const { bankName } = bankAccount;
    const message = createPaymentResult?.paymentId ? `Waiting for payment...` : `Waiting for ${bankName}`;
    if (createPaymentResult?.paymentId) {
        return <LoadingAppView title="Processing your payment…"/>;
    }
    return (<AppView style={styles.content}>
      <StatusBar barStyle="dark-content" animated/>
      <BankLogo bankAccount={bankAccount} style={styles.logo}/>
      <Spacer h={24}/>
      <Text Text-20 centered style={styles.textStatus}>
        {message}
      </Text>
      <Button brand floating title={'perPaymentLimit' in route.params ? strings.cancelButton : strings.cancelPaymentButton} onPress={onPress}/>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    content: {
        flex: 1,
        flexDirection: 'column',
        overflow: 'hidden',
        justifyContent: 'center',
    },
    progress: {
        width: rem(70),
        height: rem(70),
        margin: rem(8),
        alignSelf: 'center',
    },
    logo: {
        width: rem(86),
        height: rem(86),
        alignSelf: 'center',
    },
    textStatus: {
        marginHorizontal: rem(16),
    },
}));
export default CreateTopUpScreen;
